import React, { useEffect, useState, Suspense, lazy } from 'react';

import { ArrowLeftOutlined, OrderedListOutlined, FilePdfOutlined, ArrowRightOutlined } from '@ant-design/icons';

import { Alert, Button, Card, Col, Row, Table, notification, Spin, Typography, Descriptions, Tabs, Tag } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';

import FourOhFour from '../../pages/404';
import { Price } from '../../components/Display';
import UIStore from '../../stores/UIStore';
import { OrderPreview } from '../../components/SalesOrders';
import { OrderLineItemProps, ShipmentProps } from '../../types/types';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'dayjs';
import SalesOrderStore from '../../stores/SalesOrderStore';

import PortalLoading from '../../components/PortalLoading';
import InvoiceDownload from '../../components/SalesOrders/InvoiceDownload';

const Status = lazy(() => import('../../components/SalesOrders/Status'));

import { PageHeader } from 'components/PageHeader';

const { Title, Text } = Typography;

import { ErrorRender } from '@partsbadger/library';
import { observer } from 'mobx-react';

import QualityDocuments from 'components/SalesOrders/QualityDocuments';

import ThreeDApproval from 'components/SalesOrders/ThreeDApproval';
import { getPending3DApprovalItems, shouldHide3DApprovalColumn } from './orderDetail.helpers';

const { TabPane } = Tabs;

interface IColumnProps extends ColumnProps<OrderLineItemProps> {
    hidden: boolean;
}

export const OrderDetail: React.FC = observer((props: any) => {
    const history = useHistory();
    const location = useLocation();

    const [loading, setLoading] = useState<boolean>(false);
    const [preview, setPreview] = useState<boolean>(false);
    // const [order, setOrder] = useState<SalesOrderProps>();
    const [shipments, setShipments] = useState<Array<ShipmentProps>>([]);
    const [show3DApproval, setShow3DApproval] = useState<boolean>(false);
    const [lineItemId, setLineItemId] = useState<number>();

    const { order_items, order } = SalesOrderStore;

    const { id } = props.match.params;

    const back = () => {
        history.goBack();
    };

    const handleHashNavigation = (hash: any) => {
        // Validate that the hash is not empty and starts with '#'
        setTimeout(() => {
            if (hash && hash.startsWith('#')) {
                const targetElement = document.querySelector(hash);
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 3000);
    };

    useEffect(() => {
        const hash = window.location.hash;
        handleHashNavigation(hash);
    }, []);

    const getOrderDetails = async (id: number) => {
        await SalesOrderStore.getOrderById(id)
            .then(async data => {
                setLoading(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'topRight',
                });

                setLoading(false);
            });
        // await api.Orders.get(id).then(async data => {
        //     setOrder(data);
        //     setLineItems(data.line_items);
        //     setLoading(false);
        // });
    };
    const getShipmentDetails = async (id: any) => {
        const response = await SalesOrderStore.getShipmentsBySalesOrderId(id);
        setShipments(SalesOrderStore.shipments);
    };

    const getQualityDocuments = async (id: number) => {
        // setLoadingDocuments(true);
        await SalesOrderStore.getQualityDocumentsBySalesOrderId(id)
            .then(async data => {
                // setLoadingDocuments(false);
            })
            .catch(error => {
                // notification.error({
                //     duration: 10,
                //     message: 'Error',
                //     description: <ErrorRender error={error} />,
                //     placement: 'topRight',
                // });

                setLoading(false);
            });
    };
    useEffect(() => {
        UIStore.setSelectedKey(['orders']);
        UIStore.setDocumentTitle('Order Detail');
        setLoading(true);
        getOrderDetails(props.match.params.id);
        getShipmentDetails(props.match.params.id);
        getQualityDocuments(id);
    }, [props.match.params.id]);

    if (!order && !loading) {
        return (
            <Card bordered={false}>
                <Row className="detail-top-row">
                    <Col>
                        <ArrowLeftOutlined onClick={back} />
                        <span> ORDERS</span>
                    </Col>
                </Row>
                <FourOhFour />
            </Card>
        );
    }

    const getLineItemStatus = (data: OrderLineItemProps) => {
        if (data.quantity === 0) {
            return 'Cancelled';
        }

        if (data.original_quantity > data.quantity) {
            return 'Partially Cancelled';
        }

        return '';
    };

    const getPrice = (data: OrderLineItemProps) => {
        if (getLineItemStatus(data) == 'Cancelled') {
            return <Price style={{ textDecorationLine: 'line-through' }} value={data.original_total_after_discount} />;
        }

        if (getLineItemStatus(data) == 'Partially Cancelled') {
            return (
                <div className="flex flex-row justify-center items-baseline gap-1">
                    <Price style={{ textDecorationLine: 'line-through' }} value={data.original_total_after_discount} />

                    <div>
                        <ArrowRightOutlined style={{ fontSize: '0.7rem' }} />
                    </div>

                    <Price style={{ fontWeight: 'bolder' }} value={data.total_after_discount} />
                </div>
            );
        }

        return <Price value={data.total_after_discount} />;
    };

    function showSubTotal() {
        if ((order?.original_subtotal ?? 0) > (order?.subtotal ?? 0)) {
            return (
                <div className="flex flex-row justify-center items-baseline gap-1">
                    <Price style={{ textDecorationLine: 'line-through' }} value={order?.original_subtotal} />

                    <div>
                        <ArrowRightOutlined style={{ fontSize: '0.7rem' }} />
                    </div>

                    <Price style={{ fontWeight: 'bolder' }} value={order?.subtotal} />
                </div>
            );
        }

        return <Price value={order?.subtotal ?? 0} />;
    }

    function showGrandTotal() {
        // Both figures are built from the same shipping term. Striking through
        // a total without it next to one with it reads as a discount pointing
        // at a larger number whenever shipping outweighs what was cancelled.
        const shipping = order?.display_shipping_cost ?? 0;
        const displayTotal = order?.display_grand_total ?? (order?.grand_total ?? 0) + shipping;
        const originalTotal = (order?.original_grand_total ?? 0) + shipping;

        if (originalTotal > displayTotal) {
            return (
                <div className="flex flex-row justify-center items-baseline gap-1">
                    <Price style={{ textDecorationLine: 'line-through' }} value={originalTotal} />

                    <div>
                        <ArrowRightOutlined style={{ fontSize: '0.7rem' }} />
                    </div>

                    <Price style={{ fontWeight: 'bolder' }} value={displayTotal} />
                </div>
            );
        }

        return <Price value={displayTotal} />;
    }

    const displayShippingCost = order?.display_shipping_cost ?? 0;

    const columns: IColumnProps[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            hidden: false,
            render: (t, r) => {
                return (
                    <div>
                        <div>{t !== 'CNC Part' ? t : ''}</div>
                        <div className={'text-md'}>{r.product_description}</div>
                    </div>
                );
            },
        },
        {
            title: 'Estimated Ship Date',
            dataIndex: 'required_part_ship_date',
            key: 'required_part_ship_date',
            width: 140,
            hidden: false,
            render: (text, record) => {
                if (record.required_part_ship_date != null) {
                    return <span>{dayjs(record.required_part_ship_date).format('YYYY-MM-DD')}</span>;
                }
                return <span>{record.shipping_status}</span>;
            },
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
            width: 140,
            hidden: false,
            render: (text, record) =>
                getLineItemStatus(record) == 'Cancelled' ? (
                    <Text delete>{record.original_quantity}</Text>
                ) : getLineItemStatus(record) == 'Partially Cancelled' ? (
                    <div className="flex flex-row justify-center items-baseline gap-1">
                        <Text delete>{record.original_quantity}</Text>
                        <Text>
                            <ArrowRightOutlined style={{ fontSize: '0.7rem' }} />
                        </Text>
                        <Text strong mark>
                            {record.quantity}
                        </Text>
                    </div>
                ) : (
                    <span>{record.quantity}</span>
                ),
        },
        {
            title: 'Unit Price',
            dataIndex: 'list_price',
            key: 'list_price',
            align: 'center',
            width: 140,
            hidden: false,
            render: (text, record) => <Price value={record.list_price} />,
        },
        {
            title: 'Total Price',
            dataIndex: 'total_after_discount',
            key: 'total_after_discount',
            align: 'center',
            width: 140,
            hidden: false,
            render: (text, record) => getPrice(record),
        },
        {
            title: '3D Approval',
            dataIndex: '3d_approval',
            key: '3d_approval',
            align: 'right',
            width: 140,
            hidden: shouldHide3DApprovalColumn(order_items),
            render: (text, record) =>
                record.production_3d_file_approval_status !== null && record.production_3d_file ? (
                    <a
                        className="text-red-600"
                        onClick={() => {
                            setShow3DApproval(true);
                            setLineItemId(record.id);
                        }}
                    >
                        {record.production_3d_file_approval_status === 'Need 3D Approval' && 'Approve/Reject'}
                        {record.production_3d_file_approval_status === 'Approved' && <Tag color="green">Approved</Tag>}
                        {record.production_3d_file_approval_status === 'Rejected' && (
                            <Tag color="orange">On Revision</Tag>
                        )}
                    </a>
                ) : (
                    <>{order?.status === 'Waiting on 3d File' ? <Tag color="orange">Waiting for 3D File</Tag> : '-'}</>
                ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 140,
            hidden: (order?.original_subtotal ?? 0) > (order?.subtotal ?? 0) ? false : true,
            render: (text, record) => getLineItemStatus(record),
        },
    ];

    if (order_items.some(item => item.discount > 0.0)) {
        columns.splice(columns.length - 2, 0, {
            title: 'Discount Item',
            dataIndex: 'discount',
            key: 'discount',
            align: 'center',
            hidden: false,
            render: (text, record) => <Price value={record.discount} />,
        });
    }

    const pending3DApprovalItems = getPending3DApprovalItems(order_items);

    return (
        <>
            <PageHeader>
                <div className="flex flex-col md:flex-row justify-between container">
                    <Button type="link" icon={<ArrowLeftOutlined />} onClick={back} className="text-primary m-0 p-0">
                        Back to Order List
                    </Button>

                    <div className="d-flex flex-col md:flex-row md:ml-5" style={{ columnGap: '6px' }}>
                        <Button icon={<FilePdfOutlined />} onClick={() => setPreview(true)}>
                            Download Sales Order Confirmation
                        </Button>
                        {order?.customer_status !== 'In Review' && (
                            <>
                                <Button>
                                    <Link to={`/my-account/orders/${order?.id}/reorder`}>Reorder</Link>
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </PageHeader>

            <div
                className="container"
                data-testid="page-customer-order-detail"
                data-order-id={order?.id ?? id}
                data-ready={String(!loading && Boolean(order?.id))}
            >
                {loading ? (
                    <PortalLoading />
                ) : (
                    <div>
                        <div>
                            {pending3DApprovalItems.length > 0 && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    className="mb-3"
                                    message={
                                        pending3DApprovalItems.length === 1
                                            ? 'A 3D file is waiting for your approval'
                                            : `${pending3DApprovalItems.length} 3D files are waiting for your approval`
                                    }
                                    description="Review the 3D file(s) and approve or request changes so production can continue."
                                    action={
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => {
                                                setLineItemId(pending3DApprovalItems[0].id);
                                                setShow3DApproval(true);
                                            }}
                                        >
                                            Review 3D File
                                        </Button>
                                    }
                                />
                            )}
                            <Card
                                bodyStyle={{ paddingTop: '0.5rem' }}
                                className="shadow-sm mb-3"
                                data-testid="customer-order-detail-general-info"
                            >
                                <Title level={2}>General information</Title>
                                <Descriptions layout="horizontal" bordered size="small" column={1}>
                                    <Descriptions.Item label="Order Number">{order?.name}</Descriptions.Item>
                                    <Descriptions.Item label="PO Number">
                                        <span className="font-bold">{order?.purchase_order}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Shipping Address">
                                        <div className="flex flex-col">
                                            <span className={'value'} style={{ fontSize: '15px' }}>
                                                {order?.shipping_name}
                                            </span>
                                            <span className={'address-line'}>{order?.shipping_street}</span>
                                            <span className={'address-line'}>
                                                {order?.shipping_city} {order?.shipping_state}{' '}
                                                {order?.shipping_zip_code}
                                            </span>
                                        </div>
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Billing Address">
                                        <div className="flex flex-col">
                                            <span className={'value'} style={{ fontSize: '15px' }}>
                                                {order?.billing_name}
                                            </span>
                                            <span className={'address-line'}>{order?.billing_street}</span>
                                            <span className={'address-line'}>
                                                {order?.billing_city} {order?.billing_state} {order?.billing_zip_code}
                                            </span>
                                        </div>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            <div className="mx-20 mt-5" data-testid="customer-order-detail-status">
                                <Suspense fallback={<Spin />}>
                                    <Status order={order} shipments={props.shipments} />
                                </Suspense>
                            </div>

                            <Card
                                bodyStyle={{ paddingTop: '0.5rem' }}
                                className="shadow-sm mb-3 mt-5"
                                data-testid="customer-order-detail-line-items"
                            >
                                <div>
                                    <p className={'label'}>
                                        <OrderedListOutlined /> LINE ITEMS
                                    </p>
                                </div>

                                <Table<OrderLineItemProps>
                                    rowKey={'id'}
                                    pagination={false}
                                    columns={columns.filter(item => !item.hidden)}
                                    dataSource={order_items}
                                    footer={() => (
                                        <div style={{ marginTop: 15 }} className="flex md:justify-end">
                                            <div className={'flex flex-col w-full md:w-2/4'}>
                                                <div className="flex flex-row justify-between mb-1">
                                                    <div className="summary-label">Subtotal</div>
                                                    <div className="summary-value">{showSubTotal()}</div>
                                                </div>

                                                <div className="flex flex-row justify-between mt-1">
                                                    <div className="summary-label">Discount</div>
                                                    <div className="summary-value">
                                                        <Price value={order?.discount ?? 0} />
                                                    </div>
                                                </div>

                                                <div className="flex flex-row justify-between  mt-2">
                                                    <div className="summary-label">Tax</div>
                                                    <div className="summary-value">
                                                        <Price value={order?.taxes ?? 0} />
                                                    </div>
                                                </div>

                                                {order?.payment_terms === 'PrePaid' ||
                                                (order?.display_shipping_cost ?? 0) > 0 ? (
                                                    <div className="flex flex-row justify-between mt-2">
                                                        <div className="summary-label">Shipping</div>
                                                        <div className="summary-value">
                                                            <Price value={displayShippingCost} />
                                                        </div>
                                                    </div>
                                                ) : null}

                                                <div
                                                    className="flex flex-row justify-between  mt-2 pt-4"
                                                    style={{
                                                        borderTop: 'solid 1px #d9d9d9',
                                                    }}
                                                >
                                                    <div className="font-bold text-xl">Grand Total</div>
                                                    <div className="font-bold text-xl">{showGrandTotal()}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    scroll={{ x: 160 }}
                                    tableLayout="fixed"
                                />
                            </Card>

                            <Tabs defaultActiveKey="1" className="mt-10">
                                <TabPane
                                    tab={<span className="flex items-center font-bold">Quality Documents</span>}
                                    key="1"
                                >
                                    <div id="quality-documents">
                                        <QualityDocuments />
                                    </div>
                                </TabPane>
                                <TabPane tab={<span className="flex items-center font-bold">Invoices</span>} key="2">
                                    <Card bodyStyle={{ paddingTop: '0.5rem' }} className="shadow-sm mb-3">
                                        <InvoiceDownload />
                                    </Card>
                                </TabPane>
                            </Tabs>
                        </div>

                        {preview && (
                            <div>
                                <OrderPreview sales_order_id={order?.id} onClose={() => setPreview(false)} />
                            </div>
                        )}

                        <ThreeDApproval
                            lineItemId={lineItemId}
                            show3DApproval={show3DApproval}
                            onClose={() => {
                                setShow3DApproval(false);
                            }}
                        />
                    </div>
                )}
            </div>
        </>
    );
});
